import { EmptyState, FlexBox, Spinner } from '@dixa/lib-shared-habla';
import {
  AIInsightInstrumentPrompt,
  Instrument,
  InstrumentPanel,
  ReadInstrumentsQueryFn,
} from 'lib-analytics-types';
import {
  categorizeInstruments,
  HeatmapInstrument,
  MetricInstrument,
  MetricInstrumentsGrid,
  PresenceInstrument,
} from 'lib-analytics-ui';
import { memo, useMemo } from 'react';

import { panelGridLayoutConfig } from 'analytics/components/panels/utils/panelGridLayoutConfig';

import { UsePersistInstrumentSettingsResult } from '../../utils/usePersistInstrumentSettings';

import { AIInsightInstrumentRenderer } from './components/aiInsightInstrumentRenderer/aiInsightInstrumentRenderer';
import { ChartInstrumentRenderer } from './components/chartInstrumentRenderer/chartInstrumentRenderer';
import { TableInstrumentRenderer } from './components/tableInstrumentRenderer/tableInstrumentRenderer';
import styles from './instrumentRenderer.module.scss';

export type InstrumentRendererProps = {
  /** The containing panel. */
  panel: InstrumentPanel;
  /** The list of instruments. */
  instruments: readonly Instrument[];
  /** The list of prompts available to generate AI insights. */
  aiPrompts?: readonly AIInsightInstrumentPrompt[];
  /** The id of the API request that returned the instruments. */
  requestId?: string;
  /** Whether data is currently fetching. */
  isFetching: boolean;
  /** Whether data has failed to fetch. */
  isError: boolean;
  /** Called to refetch the instrument data. */
  onReadInstruments: ReadInstrumentsQueryFn;
  /** The method to set a specific instrument setting. */
  onSetInstrumentSetting?: UsePersistInstrumentSettingsResult['setInstrumentSetting'];
};

export const InstrumentRenderer = memo(function InstrumentRenderer({
  panel,
  instruments,
  aiPrompts,
  requestId,
  isFetching = false,
  isError = false,
  onReadInstruments,
  onSetInstrumentSetting,
}: InstrumentRendererProps): JSX.Element {
  const groups = useMemo(
    () => categorizeInstruments(instruments),
    [instruments],
  );

  const metricInstruments = useMemo((): JSX.Element | null => {
    const { metrics } = groups;
    if (!metrics.length) return null;

    return (
      <MetricInstrumentsGrid
        fluid={panel.layout.w === panelGridLayoutConfig.cols}
      >
        {metrics.map((instrument, index) => (
          <MetricInstrument
            key={`${panel.instance_id ?? panel.id}-${instrument.type}-${index}`}
            instrument={instrument}
          />
        ))}
      </MetricInstrumentsGrid>
    );
  }, [groups, panel.instance_id, panel.id, panel.layout.w]);

  const otherInstruments = useMemo((): JSX.Element | null => {
    const { chart, heatmap, table, presence, insight } = groups;

    if (chart) {
      return (
        <ChartInstrumentRenderer
          instrument={chart}
          panel={panel}
          onSetInstrumentSetting={onSetInstrumentSetting}
        />
      );
    }

    if (heatmap) return <HeatmapInstrument instrument={heatmap} />;
    if (table)
      return (
        <TableInstrumentRenderer
          instrument={table}
          panel={panel}
          onReadInstruments={onReadInstruments}
        />
      );
    if (presence) return <PresenceInstrument instrument={presence} />;

    if (aiPrompts?.length) {
      return (
        <AIInsightInstrumentRenderer
          panel={panel}
          instrument={insight}
          prompts={aiPrompts}
          requestId={requestId}
          onReadInstruments={onReadInstruments}
        />
      );
    }

    return null;
  }, [
    aiPrompts,
    groups,
    onReadInstruments,
    onSetInstrumentSetting,
    panel,
    requestId,
  ]);

  if (isError) {
    return (
      <FlexBox flex="1" justifyContent="center" alignItems="center">
        <EmptyState
          title="Failed to load data"
          description={
            <>
              <span>Something went wrong while fetching the data.</span>{' '}
              <a href="#" onClick={() => onReadInstruments()}>
                Please try again.
              </a>
            </>
          }
          icon={null}
        />
      </FlexBox>
    );
  }

  return (
    <>
      {isFetching && (
        <FlexBox
          className={styles.loader}
          flex="1"
          justifyContent="center"
          alignItems="center"
        >
          <Spinner />
        </FlexBox>
      )}

      {(metricInstruments || otherInstruments) && (
        <div className={styles.root}>
          {metricInstruments}
          {otherInstruments}
        </div>
      )}
    </>
  );
});
